import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { PageHeader, EmptyState, Chip } from "@/components/ui-bits";
import { Plus, Loader2, ArrowUp, ArrowDown, Search } from "lucide-react";
import { toast } from "sonner";

const STATUS_TONE = {
    "Editorial": "",
    "Art Direction": "",
    "Revisions": "",
    "Ready to Submit": "strong",
    "Submitted": "strong",
    "Sold": "red",
};

const STATUS_ORDER = {
    "Editorial": 0,
    "Art Direction": 1,
    "Revisions": 2,
    "Ready to Submit": 3,
    "Submitted": 4,
    "Sold": 5,
};

export default function Projects() {
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [statuses, setStatuses] = useState([]);

    const [q, setQ] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [sortKey, setSortKey] = useState("updated");
    const [sortDir, setSortDir] = useState("desc");

    const fetchAll = async () => {
        setLoading(true);
        try {
            const [rows, s] = await Promise.all([
                api.get("/projects").then((r) => r.data),
                api.get("/projects/statuses").then((r) => r.data),
            ]);
            setProjects(rows);
            setStatuses(s);
        } catch (e) {
            toast.error("Failed to load projects");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAll();
    }, []);

    const sorted = useMemo(() => {
        const cmp = (a, b) => {
            let av, bv;
            switch (sortKey) {
                case "title":
                    av = (a.title || "").toLowerCase();
                    bv = (b.title || "").toLowerCase();
                    return av.localeCompare(bv);
                case "author":
                    av = (a.author || "").toLowerCase();
                    bv = (b.author || "").toLowerCase();
                    return av.localeCompare(bv);
                case "category":
                    av = (a.category || "").toLowerCase();
                    bv = (b.category || "").toLowerCase();
                    return av.localeCompare(bv);
                case "status":
                    av = STATUS_ORDER[a.status] ?? 99;
                    bv = STATUS_ORDER[b.status] ?? 99;
                    return av - bv;
                case "round":
                    return (a.current_round || 1) - (b.current_round || 1);
                case "updated":
                default:
                    av = a.updated_at || a.created_at || "";
                    bv = b.updated_at || b.created_at || "";
                    return av.localeCompare(bv);
            }
        };
        let arr = [...projects];
        if (q) {
            const ql = q.toLowerCase();
            arr = arr.filter(
                (p) =>
                    (p.title || "").toLowerCase().includes(ql) ||
                    (p.author || "").toLowerCase().includes(ql) ||
                    (p.category || "").toLowerCase().includes(ql)
            );
        }
        if (statusFilter) arr = arr.filter((p) => p.status === statusFilter);
        arr.sort(cmp);
        if (sortDir === "desc") arr.reverse();
        return arr;
    }, [projects, sortKey, sortDir, q, statusFilter]);

    const toggleSort = (key) => {
        if (sortKey === key) {
            setSortDir(sortDir === "asc" ? "desc" : "asc");
        } else {
            setSortKey(key);
            setSortDir(key === "updated" ? "desc" : "asc");
        }
    };

    return (
        <div>
            <PageHeader
                eyebrow="Workshop"
                title="Projects"
                description="Every book in development or in front of editors. Sort by status to see what needs your attention today."
                testId="projects-header"
                right={
                    <button
                        className="btn-primary"
                        onClick={() => setDialogOpen(true)}
                        data-testid="projects-create-btn"
                    >
                        <Plus className="w-4 h-4" strokeWidth={1.5} /> New project
                    </button>
                }
            />

            {/* Filters */}
            <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 mb-6" data-testid="projects-filters">
                <div className="relative">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" strokeWidth={1.5} />
                    <input
                        className="input-flat pl-10"
                        placeholder="Search title, author, category…"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        data-testid="projects-search"
                    />
                </div>
                <select
                    className="input-flat min-w-[200px]"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    data-testid="projects-status-filter"
                >
                    <option value="">All statuses</option>
                    {statuses.map((s) => (
                        <option key={s} value={s}>{s}</option>
                    ))}
                </select>
            </div>

            {loading ? (
                <div className="text-sm font-mono text-muted-foreground pulse-mono">Loading projects…</div>
            ) : sorted.length === 0 ? (
                <EmptyState
                    title={projects.length === 0 ? "No projects yet" : "No projects match your filter"}
                    hint={
                        projects.length === 0
                            ? "Start with a title and any metadata you have. Upload manuscripts and drafts inside the project."
                            : "Try clearing the search or status filter."
                    }
                    testId="projects-empty"
                    action={
                        projects.length === 0 && (
                            <button className="btn-primary" onClick={() => setDialogOpen(true)} data-testid="projects-create-empty">
                                <Plus className="w-4 h-4" strokeWidth={1.5} /> Create your first project
                            </button>
                        )
                    }
                />
            ) : (
                <div className="border border-border rounded-sm overflow-hidden bg-white/70" data-testid="projects-table">
                    <div className="grid grid-cols-[2fr_1.2fr_0.7fr_1.1fr_0.7fr_1fr] px-4 py-3 label-mono border-b border-border bg-secondary/60 select-none">
                        <SortHeader label="Title" k="title" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                        <SortHeader label="Author" k="author" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                        <SortHeader label="Cat" k="category" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                        <SortHeader label="Status" k="status" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                        <SortHeader label="Round" k="round" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                        <SortHeader label="Updated" k="updated" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                    </div>
                    {sorted.map((p) => (
                        <Link
                            key={p.id}
                            to={`/projects/${p.id}`}
                            data-testid={`project-row-${p.id}`}
                            className="grid grid-cols-[2fr_1.2fr_0.7fr_1.1fr_0.7fr_1fr] px-4 py-3.5 border-b border-border/60 last:border-b-0 items-center text-sm hover:bg-secondary/40 transition-colors"
                        >
                            <div className="min-w-0">
                                <div className="font-serif text-lg leading-tight truncate">{p.title}</div>
                                {p.logline && (
                                    <div className="text-xs text-muted-foreground line-clamp-1 mt-0.5">{p.logline}</div>
                                )}
                            </div>
                            <div className="truncate">{p.author || <span className="text-muted-foreground">—</span>}</div>
                            <div>
                                {p.category ? <Chip>{p.category}</Chip> : <span className="text-muted-foreground text-xs">—</span>}
                            </div>
                            <div>
                                <Chip tone={STATUS_TONE[p.status] || ""}>{p.status || "Editorial"}</Chip>
                            </div>
                            <div className="label-mono">R{p.current_round || 1}</div>
                            <div className="label-mono truncate">
                                {p.updated_at ? new Date(p.updated_at).toLocaleDateString() : "—"}
                            </div>
                        </Link>
                    ))}
                </div>
            )}

            {dialogOpen && (
                <CreateDialog
                    statuses={statuses}
                    onClose={() => setDialogOpen(false)}
                />
            )}
        </div>
    );
}

function SortHeader({ label, k, sortKey, sortDir, onSort }) {
    const active = sortKey === k;
    return (
        <button
            onClick={() => onSort(k)}
            data-testid={`sort-${k}`}
            className={"flex items-center gap-1 text-left hover:text-foreground transition-colors " + (active ? "text-foreground" : "")}
        >
            <span>{label}</span>
            {active && (sortDir === "asc" ? (
                <ArrowUp className="w-3 h-3" strokeWidth={2} />
            ) : (
                <ArrowDown className="w-3 h-3" strokeWidth={2} />
            ))}
        </button>
    );
}

function CreateDialog({ onClose, statuses }) {
    const nav = useNavigate();
    const [form, setForm] = useState({
        title: "",
        author: "",
        category: "",
        age_range: "",
        word_count: "",
        logline: "",
        status: "Editorial",
    });
    const [busy, setBusy] = useState(false);

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const submit = async () => {
        if (!form.title.trim()) return toast.error("Title is required");
        setBusy(true);
        try {
            const payload = {
                ...form,
                word_count: form.word_count ? parseInt(form.word_count) : null,
            };
            const res = await api.post("/projects", payload);
            toast.success("Project created");
            nav(`/projects/${res.data.id}`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to create");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="project-create-dialog">
            <div className="paper border border-border rounded-sm w-full max-w-2xl bg-background p-8">
                <div className="label-mono mb-3">New Project</div>
                <h3 className="font-serif text-3xl mb-6">Tell me about the manuscript</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Field label="Title *" testId="create-title">
                        <input className="input-flat" value={form.title} onChange={(e) => set("title", e.target.value)} />
                    </Field>
                    <Field label="Author" testId="create-author">
                        <input className="input-flat" value={form.author} onChange={(e) => set("author", e.target.value)} />
                    </Field>
                    <Field label="Category" testId="create-category">
                        <select className="input-flat" value={form.category} onChange={(e) => set("category", e.target.value)}>
                            <option value="">—</option>
                            {["PB", "BB", "ER", "Chapter books", "MG", "YA", "Graphic Novel", "Adult", "Nonfiction"].map((c) => (
                                <option key={c} value={c}>{c}</option>
                            ))}
                        </select>
                    </Field>
                    <Field label="Status" testId="create-status">
                        <select className="input-flat" value={form.status} onChange={(e) => set("status", e.target.value)}>
                            {(statuses.length ? statuses : ["Editorial"]).map((s) => (
                                <option key={s} value={s}>{s}</option>
                            ))}
                        </select>
                    </Field>
                    <Field label="Age range" testId="create-age">
                        <input
                            className="input-flat"
                            placeholder="e.g. 8-12"
                            value={form.age_range}
                            onChange={(e) => set("age_range", e.target.value)}
                        />
                    </Field>
                    <Field label="Word count" testId="create-words">
                        <input
                            className="input-flat"
                            type="number"
                            value={form.word_count}
                            onChange={(e) => set("word_count", e.target.value)}
                        />
                    </Field>
                    <Field label="Logline" testId="create-logline" full>
                        <textarea
                            className="input-flat min-h-[80px]"
                            value={form.logline}
                            onChange={(e) => set("logline", e.target.value)}
                            placeholder="One-sentence hook…"
                        />
                    </Field>
                </div>
                <div className="flex gap-2 justify-end mt-6">
                    <button className="btn-ghost" onClick={onClose} data-testid="create-cancel">Cancel</button>
                    <button className="btn-primary disabled:opacity-60" onClick={submit} disabled={busy} data-testid="create-submit">
                        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" strokeWidth={1.5} />}
                        {busy ? "Creating…" : "Create"}
                    </button>
                </div>
            </div>
        </div>
    );
}

function Field({ label, children, full, testId }) {
    return (
        <label className={"block " + (full ? "md:col-span-2" : "")} data-testid={testId}>
            <span className="label-mono">{label}</span>
            <div className="mt-2">{children}</div>
        </label>
    );
}
